import React, { useState, useEffect, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  Select,
  Checkbox,
  Tooltip as AntdTooltip,
  Typography,
  Row,
  Col,
  Divider,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const Count = ({ streetName, data }) => {
  const options = ["1min", "5min", "10min", "15min", "30min"];
  const defaultSelectedOption = "10min";
  const [totOutputCount, setTotOutputCount] = useState(null);
  const [ticks, setTicks] = useState({});
  const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);
  const [extractedgraphData, setExtractedgraphData] = useState([]);
  const [vehicleVisibility, setVehicleVisibility] = useState({});
  const [availableVehicleTypes, setAvailableVehicleTypes] = useState([]);

  // Define colors for different vehicle types
  const colors = {
    Totale: "#16A34A",  // Green
    Car: "#2563EB",     // Blue
    Truck: "#EA580C",   // Orange
    BRT: "#9333EA",     // Purple
  };

  // Helper to normalize IDs (handles "455.0" vs 455 format)
  const normalizeId = (id) => {
    if (id === null || id === undefined) return null;
    const parsed = typeof id === 'string' ? parseFloat(id) : id;
    return Number.isInteger(parsed) ? parsed : Math.floor(parsed);
  };

  useEffect(() => {
    if (streetName) {
      // Normalize streetName for matching
      const normalizedStreetName = normalizeId(streetName);
      const streetData = data.find((street) => normalizeId(street.id) === normalizedStreetName);

      if (streetData && streetData.indicatoriGlobali) {
        const totInputCountFromData =
          streetData.indicatoriGlobali.totOutputCount;
        setTotOutputCount(totInputCountFromData);
      }

      if (
        streetData &&
        streetData.serieTemporale &&
        streetData.serieTemporale.inputCount &&
        streetData.serieTemporale.inputCount[selectedOption]
      ) {
        const sourceData = streetData.serieTemporale.inputCount[selectedOption];
        const processedData = [];

        // Get all available vehicle types dynamically
        const vehicleTypes = Object.keys(sourceData);
        setAvailableVehicleTypes(vehicleTypes);

        // Initialize visibility for all vehicle types
        const initialVisibility = {};
        vehicleTypes.forEach(type => {
          initialVisibility[type] = true;
        });
        setVehicleVisibility(initialVisibility);

        // Use the first vehicle type to get time keys
        const firstType = vehicleTypes[0];
        if (firstType && sourceData[firstType]) {
          Object.keys(sourceData[firstType]).forEach(timeKey => {
            const time = parseFloat(timeKey);
            if (!isNaN(time)) {
              const point = { time };
              // Add all vehicle types to the point
              vehicleTypes.forEach(vType => {
                const key = vType.toLowerCase();
                point[key] = sourceData[vType]?.[timeKey] ?? 0;
              });
              processedData.push(point);
            }
          });
        }

        // Sort by time
        processedData.sort((a, b) => a.time - b.time);
        setExtractedgraphData(processedData);
      } else {
        setExtractedgraphData([]);
        setAvailableVehicleTypes([]);
      }

      const newTicks = {
        "1min": [10, 20, 30, 40, 50, 60],
        "5min": [10, 20, 30, 40, 50, 60],
        "10min": [10, 20, 30, 40, 50, 60],
        "15min": [15, 30, 45, 60],
        "30min": [30, 40, 50, 60],
      };
      setTicks(newTicks);
    }
  }, [streetName, selectedOption, data]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setVehicleVisibility((prevVisibility) => ({
      ...prevVisibility,
      [name]: checked,
    }));
  };

  return (
    <div style={{ padding: "16px" }}>
      {/* Header */}
      <Row align="middle" gutter={8}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Veicoli
          </Title>
        </Col>
        <Col>
          <AntdTooltip
            color="#768079"
            title="Numero di veicoli che hanno completato l'attraversamento della sezione stradale nell'unità di tempo selezionata.
Il periodo di analisi viene suddiviso in intervalli di tempo consecutivi, la durata di questi può essere selezionata tramite l'apposito menu a tendina."
          >
            <InfoCircleOutlined style={{ fontSize: 18, color: "#555" }} />
          </AntdTooltip>
        </Col>
      </Row>

      <Divider />

      {/* Chart + Filters */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={18}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart margin={{ top: 5, right: 30, left: 20, bottom: 25 }} data={extractedgraphData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis
                dataKey="time"
                allowDataOverflow={true}
                type="number"
                ticks={ticks[selectedOption]}
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 10, fill: '#999' }}
                label={{
                  value: "Tempo",
                  position: "insideBottom",
                  offset: -10,
                  fill: '#999',
                  fontSize: 12
                }}
              />
              <YAxis
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 10, fill: '#999' }}
                label={{
                  value: "Veicoli",
                  angle: -90,
                  position: "insideLeft",
                  fill: '#999',
                  fontSize: 12
                }}
              />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                itemStyle={{ color: '#333', fontWeight: 600 }}
                formatter={(value) => Number(value).toFixed(2)}
              />
              {availableVehicleTypes.map(vehicleType => (
                vehicleVisibility[vehicleType] && (
                  <Area
                    key={vehicleType}
                    type="stepAfter"
                    dataKey={vehicleType.toLowerCase()}
                    name={vehicleType}
                    stroke={colors[vehicleType] || '#666666'}
                    fillOpacity={0}
                    strokeWidth={3}
                  />
                )
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Col>

        {/* Checkboxes + Total */}
        <Col xs={24} md={6}>
          <div style={{ marginBottom: 16 }}>
            {availableVehicleTypes.map((vehicleType) => (
              <div key={vehicleType}>
                <Checkbox
                  checked={vehicleVisibility[vehicleType]}
                  onChange={handleCheckboxChange}
                  name={vehicleType}
                  style={{ color: colors[vehicleType] || '#666666' }}
                >
                  <Text style={{ color: colors[vehicleType] || '#666666' }}>{vehicleType}</Text>
                </Checkbox>
              </div>
            ))}
          </div>

          <Divider />

          <Title level={5}>{`Tot Veicoli: ${totOutputCount ?? 0}`}</Title>
        </Col>
      </Row>

      {/* Select */}
      <Row justify="center" style={{ marginTop: 24 }}>
        <Select
          value={selectedOption}
          onChange={setSelectedOption}
          options={options.map((o) => ({ value: o, label: o }))}
          style={{ width: 160 }}
        />
      </Row>
    </div>
  );
};

export default Count;
